import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChallengeActivityQuery,
  ChallengeActivityQuestionAttemptQuery,
  ChallengeActivityService,
  ChallengeActivityStoreModel,
} from '@examdojo/challenge';
import {
  McqAttemptResponseService,
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
  QuestionAttemptEntityStoreModel,
} from '@examdojo/question';
import { AssignmentSectionQuestionQuery, AssignmentService } from '@examdojo/assignment';
import { catchError, EMPTY, switchMap, of, combineLatest, finalize } from 'rxjs';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { ExamdojoPlatformService } from '../../../shared/platform.service';

@Injectable()
export class SyncChallengeActivityStateService {
  constructor(
    private readonly challengeActivityService: ChallengeActivityService,
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly questionAttemptEntityService: QuestionAttemptEntityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly platformService: ExamdojoPlatformService,
    private readonly challengeActivityQuestionAttemptQuery: ChallengeActivityQuestionAttemptQuery,
    private readonly assignmentSectionQuestionQuery: AssignmentSectionQuestionQuery,
    private readonly assignmentService: AssignmentService,
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
    private readonly mcqResponseService: McqAttemptResponseService,
  ) {
    const isChallengeEnabled = this.featureFlagService.get(ExamDojoFeatureFlag.Exams);

    if (isChallengeEnabled) {
      this.syncStoreWhenBackOnline().pipe(takeUntilDestroyed()).subscribe();
    }
  }

  private syncStoreWhenBackOnline() {
    return this.platformService.trigger$.pipe(
      switchMap(() =>
        combineLatest([this.syncChallengeActivityState(), this.assignmentService.fetchAssignmentsWithSections()]),
      ),
      catchError(() => EMPTY),
    );
  }

  private syncChallengeActivityState() {
    const localOngoing = this.challengeActivityQuery.getOngoingActivity();

    return this.challengeActivityService.fetchAll().pipe(
      switchMap(() => {
        const remoteOngoing = this.challengeActivityQuery.getOngoingActivity();

        if (!remoteOngoing || !localOngoing || localOngoing.id !== remoteOngoing.id) {
          // different activity will be handled by the separate listener
          return of(null);
        }

        const localQuestionAttempts = this.questionAttemptQuery.getAllEntities();

        if (remoteOngoing.status === 'SUBMITTED') {
          this.questionAttemptEntityService.markAllPendingAttemptsAsSubmittedLocally();
        }

        return combineLatest([
          this.challengeActivityService.fetchMissingQuestionAttempts(remoteOngoing.id),
          this.shouldUpdateExistingQuestionAttempts(remoteOngoing.id)
            ? this.updateGradingsForExistingQuestionAttempts(localQuestionAttempts)
            : of(true),
          this.syncMcqResponses(),
        ]).pipe(
          finalize(() => {
            if (remoteOngoing.status === 'SUBMITTED') {
              this.questionAttemptEntityService.markAllPendingAttemptsAsSubmittedLocally();
            }
          }),
        );
      }),
    );
  }

  private shouldUpdateExistingQuestionAttempts(challengeActivityId: ChallengeActivityStoreModel['id']): boolean {
    const challengeActivityQuestionAttempts =
      this.challengeActivityQuestionAttemptQuery.getByChallengeActivityId(challengeActivityId);
    const questionAttempts = this.questionAttemptQuery.getAllEntities();
    const assignmentSectionQuestions = this.assignmentSectionQuestionQuery.getAllEntities();
    const allQuestionAttemptsPresent =
      challengeActivityQuestionAttempts.length === questionAttempts.length &&
      assignmentSectionQuestions.length - questionAttempts.length === 0;

    const allQuestionAttemptAlreadyGradedOrFailed = questionAttempts.every(
      (attemptEntity) => attemptEntity.attempt.status === 'GRADED' || attemptEntity.attempt.status === 'FAILED',
    );

    return !allQuestionAttemptsPresent || !allQuestionAttemptAlreadyGradedOrFailed;
  }

  private updateGradingsForExistingQuestionAttempts(localQuestionAttempts: QuestionAttemptEntityStoreModel[]) {
    if (localQuestionAttempts.length === 0) {
      return of(null);
    }

    return combineLatest(
      localQuestionAttempts.map((questionAttempt) =>
        this.questionAttemptEntityService.updateQuestionAttemptWithGradings(questionAttempt.id),
      ),
    );
  }

  private syncMcqResponses() {
    const mcqAttemptIds = this.questionAttemptQuery
      .getAllEntities()
      .filter((entity) => this.questionAttemptQuery.isQuestionMultipleChoice(entity.id))
      .map((entity) => entity.id);

    if (mcqAttemptIds.length === 0) {
      return of(null);
    }

    return this.mcqResponseService.fetchByQuestionAttemptIds(mcqAttemptIds);
  }
}
